import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ClearIcon from '@mui/icons-material/Clear';
import VideocamIcon from '@mui/icons-material/Videocam';
import YouTubeIcon from '@mui/icons-material/YouTube';
import { Checkbox, FormControl, InputLabel, MenuItem } from '@mui/material';
import { useTranslation } from 'next-i18next';
import React, { useState } from 'react';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import { Controller, useForm } from 'react-hook-form';
import ReactPlayer from 'react-player';
import toast from 'react-hot-toast';
import { useLoader } from '../../../../../HOC/LoaderProvider';
import { useBooth } from '../../../../../hooks/useBooth';
import { useMobileApp } from '../../../../../hooks/useMobileApp';
import type { Booth, VideoType, WelcomeVideo } from '../../../../../interfaces/interfaces';
import { fetchAndSetAdminBooth, setAdminBoothVideos } from '../../../../../reduxToolkit/slices/adminSlice';
import { setBoothVideos } from '../../../../../reduxToolkit/slices/commonSlice';
import { useDispatch, useSelector } from '../../../../../reduxToolkit/typedRedux';
import { createDisplayLink } from '../../../../../services/helper';
import { updateBooth } from '../../../../../services/services';
import validationBuilder from '../../../../../static/validations';
import VyButton from '../../../../common/VyButton';
import VySelect from '../../../../common/VySelect';
import VyTextField from '../../../../common/VyTextField';
import VyTooltip from '../../../../common/VyTooltip';
import VyUpload from '../../../../common/VyUpload';
import VideoPreview from './VideoPreview';
import VideoComponent from './VideoRecording';

interface FormProps {
  welcomeVideo: string;
}

const BoothVideos = ({ booth: propBooth, isInline = false, onRefresh }: { booth?: Booth; isInline?: boolean; onRefresh?: () => void }) => {
  const booth = propBooth ?? useSelector((state) => state.admin.userbooth);
  const event = useSelector((state) => state.admin.event ?? state.common.event);

  const { t } = useTranslation(['booth', 'common', 'account', 'toasts']);
  const validations = validationBuilder(t);
  const dispatch = useDispatch();
  const youtubeForm = useForm<FormProps>();
  const { startLoader, stopLoader } = useLoader();
  const { fetchAndUpdateBooth } = useBooth();
  const { isAppView } = useMobileApp();

  const [selectedVideoIndex, setSelectedVideo] = useState(0);
  const [isSavingYt, setIsSavingYt] = useState(false);

  const selectedVideo = booth?.boothVideos?.[selectedVideoIndex];
  const isUploadDisabled = !event?.modules?.preuploadedmedia?.settings?.boothVideoUpload;

  if (!booth) return <></>;

  const getVideosArray = () => (booth.boothVideos ? [...booth.boothVideos] : []);

  const addVideo = () => {
    const boothVideos = [...getVideosArray(), { type: 'recording', video: '', isNew: true, autoplay: false } as WelcomeVideo];
    updateGlobalState(boothVideos);
    setSelectedVideo(getVideosArray().length);
  };

  const updateGlobalState = (videos: WelcomeVideo[]) => {
    if (!propBooth) dispatch(setAdminBoothVideos(videos));
    else dispatch(setBoothVideos({ boothid: booth._id, boothVideos: videos }));
  };

  const removeVideo = async (index: number) => {
    const newVideos = getVideosArray();
    newVideos.splice(index, 1);
    await updateBoothWithVideos(newVideos);
    dispatch(setAdminBoothVideos(newVideos));
    setSelectedVideo(0);
    onRefresh?.();
  };

  const changeWelcomeVideoType = (type: VideoType) => {
    const newVideos: WelcomeVideo[] = getVideosArray();
    newVideos[selectedVideoIndex] = { ...newVideos[selectedVideoIndex], type };
    updateGlobalState(newVideos);
  };

  const changeWelcomeVideoThumbnail = async (fileid: string | null) => {
    const newVideos = getVideosArray();
    newVideos[selectedVideoIndex] = { ...newVideos[selectedVideoIndex], thumbnail: fileid };
    await updateSelectedVideoSlot(newVideos[selectedVideoIndex]);
    dispatch(setAdminBoothVideos(newVideos));
    onRefresh?.();
  };

  const updateSelectedVideoSlot = async (welcomeVideoProps: Partial<WelcomeVideo>) => {
    !isInline && startLoader();
    const newVideos = getVideosArray();
    newVideos[selectedVideoIndex] = { ...newVideos[selectedVideoIndex], ...welcomeVideoProps };
    await updateBoothWithVideos(newVideos);
    updateGlobalBoothState();
    !isInline && stopLoader();
    onRefresh?.();
    toast.success(t('toasts:success'));
  };

  const updateBoothWithVideos = (newVideos: WelcomeVideo[]) => {
    return updateBooth({
      boothid: booth._id,
      boothVideos: newVideos,
    });
  };

  const submitYoutubeVideo = async ({ welcomeVideo }: FormProps) => {
    setIsSavingYt(true);
    await updateSelectedVideoSlot({ type: 'youtube', video: welcomeVideo, thumbnail: selectedVideo?.thumbnail });
    setIsSavingYt(false);
  };

  const updateRecordingVideo = (videoString: string) => {
    updateSelectedVideoSlot({ type: 'recording', video: videoString, thumbnail: selectedVideo?.thumbnail });
  };

  const handleAutoplayChange = (autoplay: boolean) => {
    updateSelectedVideoSlot({ autoplay });
  };

  const onDragEnd = async ({ destination, draggableId }: DropResult) => {
    if (!destination) return;
    const draggedIndex = parseInt(draggableId);
    if (draggedIndex === destination.index) return;
    !isInline && startLoader();
    const videos = getVideosArray();
    const [videoDragged] = videos.splice(draggedIndex, 1);
    videos.splice(destination.index, 0, videoDragged);
    await updateBoothWithVideos(videos);
    updateGlobalBoothState();
    !isInline && stopLoader();
    onRefresh?.();
    toast.success(t('toasts:success'));
  };

  const updateUploadVideo = async (videoString: string | null) => {
    if (!videoString || !selectedVideo) return;
    try {
      await updateSelectedVideoSlot({ type: 'vystemboothvideo', video: videoString, thumbnail: selectedVideo.thumbnail });
      updateGlobalBoothState();
      toast(t('toasts:vystem-video-render'));
    } catch (err) {
      console.error('err', err);
    }
  };

  const updateGlobalBoothState = () => {
    if (isInline) fetchAndUpdateBooth(booth._id);
    else dispatch(fetchAndSetAdminBooth({ boothid: booth._id }));
  };

  /**
   * The react player cannot handle youtube live urls atm - therefore we replace this
   * with an iframe
   * @param url
   * @returns
   */
  const testYouTubeLive = (url: string) => /youtube.com\/embed\/live_stream\?channel=/.test(url);

  const aspectPlayer = (url: string, thumbnailUrl?: string) => (
    <>
      <div className="h-0 pt-[56.35%] relative">
        <div className="absolute top-0 left-0 w-full h-full">
          {testYouTubeLive(url) ? (
            <iframe width="100%" height="100%" src={url} />
          ) : (
            <ReactPlayer
              playsinline={isAppView()}
              light={thumbnailUrl}
              width="100%"
              height="100%"
              className="h-full overflow-hidden rounded-md"
              controls
              url={url}
            />
          )}
        </div>
      </div>
    </>
  );

  return (
    <>
      <div
        className="flex flex-col w-full gap-4 mx-4 my-0 rounded-sm outline-none focus:ring-2 ring-offset-4 ring-vystemblue"
        id="boothVideos-focus"
        tabIndex={0}>
        <div className="flex p-1">
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="droppable-filter-values" direction="horizontal">
              {(provided) => (
                <div className="flex" {...provided.droppableProps} ref={provided.innerRef}>
                  {booth.boothVideos?.map((wVideo, index) => (
                    <Draggable key={`mVideo-${index}`} draggableId={index.toString()} index={index}>
                      {(provided) => (
                        <VyTooltip key={`mVideo-${index}`} title={t('booth:rearrange-icon')}>
                          <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps} className="relative">
                            <VideoPreview
                              onClick={() => setSelectedVideo(index)}
                              className={
                                (index === selectedVideoIndex ? 'border border-black ' : ' ') + (wVideo.type === 'youtube' ? 'bg-red-400' : 'bg-blue-400')
                              }>
                              {wVideo.type === 'youtube' ? <YouTubeIcon /> : <VideocamIcon />}
                            </VideoPreview>

                            <button
                              type="button"
                              onClick={() => removeVideo(index)}
                              style={{ top: '-9px', right: '-2px' }}
                              className="absolute grid w-5 h-5 rounded-full bg-slate-700 hover:bg-slate-900 place-items-center">
                              <ClearIcon style={{ fontSize: '0.75rem', color: 'white' }} />
                            </button>
                          </div>
                        </VyTooltip>
                      )}
                    </Draggable>
                  ))}
                </div>
              )}
            </Droppable>
          </DragDropContext>
          <VideoPreview onClick={addVideo} className="text-white bg-slate-500">
            +
          </VideoPreview>
          {booth?.boothVideos?.length === 0 && (
            <div className="flex items-center animate-pulse">
              <ArrowBackIcon />
              <span className="font-bold">{t('account:add-video')}</span>
            </div>
          )}
        </div>
        {booth && booth.boothVideos && booth.boothVideos.length > 0 && (
          <div className="flex flex-col justify-between w-full space-y-2 text-center">
            <FormControl variant="outlined">
              <InputLabel id="video-type">{t('booth:type')}</InputLabel>
              <VySelect
                label="Type"
                labelId="video-type"
                disabled={!selectedVideo?.isNew}
                onChange={(e) => changeWelcomeVideoType(e.target.value as VideoType)}
                value={selectedVideo?.type ? selectedVideo.type : 'recording'}>
                <MenuItem value="youtube">Youtube</MenuItem>
                <MenuItem value="recording">{t('booth:record')}</MenuItem>
                {!isUploadDisabled && <MenuItem value="vystemboothvideo">{t('booth:upload')}</MenuItem>}
              </VySelect>
            </FormControl>
            <div className="w-full h-full">
              {selectedVideo && selectedVideo.type === 'recording' && (
                <div className="w-full h-56 overflow-hidden bg-black rounded-3xl">
                  <VideoComponent thumbnail={selectedVideo.thumbnail} currentVideo={selectedVideo.video} booth={booth} updateVideo={updateRecordingVideo} />
                </div>
              )}
              {selectedVideo && selectedVideo.type === 'youtube' && (
                <>
                  {!selectedVideo?.video ? (
                    <form onSubmit={youtubeForm.handleSubmit(submitYoutubeVideo)} className="flex flex-col items-center justify-center space-y-2">
                      <label className="flex flex-col w-full text-center">
                        <span className="text-lg font-bold">Youtube Link</span>
                        <Controller
                          control={youtubeForm.control}
                          name="welcomeVideo"
                          defaultValue={selectedVideo && selectedVideo.video}
                          render={({ field }) => (
                            <VyTextField
                              {...field}
                              className="w-7/12 p-1 mx-0 my-4"
                              register={{ ...youtubeForm.register('welcomeVideo', validations('youtubeVideo', true)) }}
                            />
                          )}
                        />
                      </label>
                      <VyButton isSaveButton showLoadingIcon={isSavingYt} disabled={isSavingYt} data-cy="welcome-submit" type="submit">
                        {t('common:save')}
                      </VyButton>
                      {youtubeForm.formState.errors.welcomeVideo && <small>{youtubeForm.formState.errors.welcomeVideo.message}</small>}
                    </form>
                  ) : (
                    aspectPlayer(selectedVideo.video, selectedVideo.thumbnail ? createDisplayLink(selectedVideo.thumbnail) : undefined)
                  )}
                </>
              )}
              {selectedVideo && selectedVideo.type === 'vystemboothvideo' && (
                <>
                  <div className="flex flex-col items-center justify-center">
                    {!selectedVideo.video && (
                      <>
                        Max. 400 MB mp4
                        <VyUpload
                          maxFileMb={400}
                          showFileUploadProgress
                          onChange={updateUploadVideo}
                          randomExternalImageButton={false}
                          acceptedFileTypes="video/mp4"
                          showPreview={false}
                        />
                      </>
                    )}
                  </div>
                  {/* selectedVideo.video && !selectedVideo.videoApiId && <>{t('video-being-processed')}</> */}
                  {selectedVideo.muxId &&
                    selectedVideo.muxPlaybackIds?.length === 1 &&
                    aspectPlayer(
                      `https://stream.mux.com/${selectedVideo.muxPlaybackIds[0].id}.m3u8`,
                      selectedVideo.thumbnail ? createDisplayLink(selectedVideo.thumbnail) : undefined
                    )}

                  {selectedVideo.videoApiId &&
                    !selectedVideo.muxId &&
                    aspectPlayer(
                      `https://cdn.api.video/vod/${selectedVideo.videoApiId}/hls/manifest.m3u8`,
                      selectedVideo.thumbnail ? createDisplayLink(selectedVideo.thumbnail) : undefined
                    )}
                </>
              )}
            </div>
            <label className="flex items-center">
              <VyTooltip title={t('booth:autoplay')}>
                <span>{t('booth:autoplay')}</span>
              </VyTooltip>
              <Checkbox checked={selectedVideo?.autoplay} value={selectedVideo?.autoplay} onChange={(_, checked) => handleAutoplayChange(checked)} />
            </label>

            <label className="flex flex-col">
              <VyTooltip style={{ textAlign: 'left' }} title={t('account:tooltip-mediaThumbnail')}>
                {t('account:thumbnail')}
              </VyTooltip>
              <VyUpload
                defaultFileId={selectedVideo?.thumbnail}
                onChange={(file: string | null) => changeWelcomeVideoThumbnail(file)}
                randomExternalImageButton={false}
                acceptedFileTypes="image/*"
                showPreview={false}
                withCrop
                showCropButton={false}
                cropAspectRatio={16 / 9}
                style={{ borderRadius: '4px' }}
              />
            </label>
          </div>
        )}
      </div>
    </>
  );
};

export default BoothVideos;
